import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Globe, Users, Trophy, ChevronRight, Star } from 'lucide-react';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: <Heart className="text-[#8ca87c]" size={28} />,
      title: 'Passion for Padel',
      description: 'We live and breathe padel. Our team consists of players, coaches, and enthusiasts who understand what makes a great padel experience.',
    },
    {
      icon: <Globe className="text-[#8ca87c]" size={28} />,
      title: 'International Community',
      description: 'Connect with padel players from around the world. Our trips bring together people from diverse backgrounds united by their love of the sport.',
    },
    {
      icon: <Users className="text-[#8ca87c]" size={28} />,
      title: 'Player-First Approach',
      description: 'Every decision we make prioritizes the player experience. From court quality to accommodation, we never compromise.',
    },
    {
      icon: <Trophy className="text-[#8ca87c]" size={28} />,
      title: 'Excellence in Everything',
      description: 'We partner with the best facilities, coaches, and venues to deliver consistently exceptional experiences.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Happy Players' },
    { value: '50+', label: 'Trips Organized' },
    { value: '15', label: 'Partner Academies' },
    { value: '12', label: 'Countries Represented' },
  ];

  return (
    <div className="bg-[#f5f3ee]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/pox9gsl3_WhatsApp%20Image%202026-02-07%20at%2012.21.45%20%281%29.jpeg"
            alt="Group of padel players"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 border border-[#f5f3ee]/40 text-[#f5f3ee] text-xs tracking-[0.3em] uppercase mb-8">
              Our Story
            </span>
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#f5f3ee] mb-6">
              More Than Just<br />
              <span className="italic">A Trip</span>
            </h1>
            <p className="text-lg text-[#f5f3ee]/80 max-w-xl mx-auto font-light">
              We're building a global community of padel lovers, one unforgettable trip at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-32" data-testid="our-story">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
                About Us
              </span>
              <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
                Born From a<br />
                <span className="italic">Love of Padel</span>
              </h2>
              <div className="space-y-6 text-base text-[#414b3b]/60 font-light leading-relaxed">
                <p>
                  Padel Holidays Algarve started with a simple idea: combine our passion for padel 
                  with our love for travel and create experiences that bring players together from 
                  around the world.
                </p>
                <p>
                  After years of traveling to play padel in different countries and struggling to 
                  find quality, well-organized experiences, we decided to create our own. We 
                  wanted to build something special – a community where players could improve 
                  their game, make lasting friendships, and enjoy incredible destinations.
                </p>
                <p>
                  Today, we're proud to host players from across Europe and beyond, united by 
                  their love of padel and adventure. The Algarve is just the beginning – we 
                  have big plans for the future.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="overflow-hidden aspect-[3/4]">
                    <img
                      src="https://images.unsplash.com/photo-1563393932010-ddd879028ea2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxBbGdhcnZlJTIwUG9ydHVnYWwlMjBiZWFjaCUyMHN1bnNldCUyMGxhbmRzY2FwZSUyMG5vJTIwcGVvcGxlfGVufDB8fHx8MTc3MDU1MjAxN3ww&ixlib=rb-4.1.0&q=85"
                      alt="Algarve beach at dusk"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="pt-8 space-y-4">
                  <div className="overflow-hidden aspect-[3/4]">
                    <img
                      src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/ofmg81ra_WhatsApp%20Image%202026-02-08%20at%2022.04.04%20%287%29.jpeg"
                      alt="Pool and padel courts"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#414b3b]" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <span className="font-heading font-light text-4xl md:text-5xl text-[#8ca87c] block mb-2">
                  {stat.value}
                </span>
                <span className="text-[#f5f3ee]/60 font-light text-sm">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32" data-testid="values-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              What Drives Us
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Our Values
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 md:p-10"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 bg-[#f5f3ee] flex items-center justify-center mb-6">
                  {value.icon}
                </div>
                <h3 className="font-heading text-2xl text-[#414b3b] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#414b3b]/60 font-light leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who's Behind It */}
      <section className="py-24 md:py-32 bg-white" data-testid="founder-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="overflow-hidden aspect-[3/4] max-w-md mx-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/pox9gsl3_WhatsApp%20Image%202026-02-07%20at%2012.21.45%20%281%29.jpeg"
                  alt="Founder"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
                Who's Behind It
              </span>
              <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
                Meet Your<br />
                <span className="italic text-[#414b3b]/50">Coach & Guide</span>
              </h2>
              <p className="text-base text-[#414b3b]/60 mb-8 font-light leading-relaxed">
                With a passion for padel and years of experience at the highest level, 
                I created Padel Holidays Algarve to share the sport I love with players 
                from around the world.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4 bg-[#f5f3ee] p-4">
                  <div className="w-12 h-12 bg-[#8ca87c] flex items-center justify-center flex-shrink-0">
                    <Trophy size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="font-heading text-lg text-[#414b3b] block">Top 20 Best Players</span>
                    <span className="text-sm text-[#414b3b]/50">of Portugal</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#f5f3ee] p-4">
                  <div className="w-12 h-12 bg-[#8ca87c] flex items-center justify-center flex-shrink-0">
                    <Globe size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="font-heading text-lg text-[#414b3b] block">FIP Player</span>
                    <span className="text-sm text-[#414b3b]/50">International Padel Federation</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#f5f3ee] p-4">
                  <div className="w-12 h-12 bg-[#8ca87c] flex items-center justify-center flex-shrink-0">
                    <Users size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="font-heading text-lg text-[#414b3b] block">+8 Years</span>
                    <span className="text-sm text-[#414b3b]/50">Coaching Experience</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#8ca87c]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Join Our Community
            </h2>
            <p className="text-base text-[#414b3b]/70 mb-10 font-light">
              Be part of something special. Book your trip and become part of the 
              Padel Holidays family.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-14 px-10 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#333d2e]"
                data-testid="about-book-btn"
              >
                Book Your Trip
                <ChevronRight size={18} />
              </Link>
              <Link
                to="/experiences"
                className="h-14 px-10 border border-[#414b3b] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:bg-[#414b3b] hover:text-[#f5f3ee]"
                data-testid="about-packages-btn"
              >
                View Packages
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
